import React, { useState, useMemo } from 'react';
import { CheckIcon, SearchIcon, UserIcon } from 'lucide-react';
import type { ClientRecord } from '../../hooks/useClients';

interface ClientSelectorListProps {
  clients: ClientRecord[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export function ClientSelectorList({
  clients,
  selectedIds,
  onToggle,
  onSelectAll,
  onDeselectAll,
}: ClientSelectorListProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter clients by search
  const filteredClients = useMemo(() => {
    if (!searchQuery.trim()) return clients;

    const query = searchQuery.toLowerCase();
    return clients.filter(
      (client) =>
        client.name.toLowerCase().includes(query) ||
        client.phone.toLowerCase().includes(query)
    );
  }, [clients, searchQuery]);

  // Group clients by status
  const groupedClients = useMemo(() => {
    const groups: {
      active: ClientRecord[];
      leads: ClientRecord[];
      other: ClientRecord[];
    } = {
      active: [],
      leads: [],
      other: [],
    };

    filteredClients.forEach((client) => {
      if (client.status === 'active') {
        groups.active.push(client);
      } else if (client.status === 'lead') {
        groups.leads.push(client);
      } else {
        groups.other.push(client);
      }
    });

    return groups;
  }, [filteredClients]);

  const totalCount = filteredClients.length;
  const selectedCount = selectedIds.size;
  const allSelected = totalCount > 0 && selectedCount === totalCount;

  return (
    <div className="space-y-3">
      {/* Search Bar */}
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent text-sm"
        />
      </div>

      {/* Select All / Deselect All */}
      <div className="flex gap-2">
        <button
          onClick={onSelectAll}
          disabled={allSelected}
          className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Select All ({totalCount})
        </button>
        <button
          onClick={onDeselectAll}
          disabled={selectedCount === 0}
          className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Deselect All
        </button>
      </div>

      {/* Client List */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {/* Active Clients */}
        {groupedClients.active.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Active Clients ({groupedClients.active.length})
            </h3>
            <div className="space-y-1">
              {groupedClients.active.map((client) => (
                <ClientCheckboxItem
                  key={client.id}
                  client={client}
                  isSelected={selectedIds.has(client.id)}
                  onToggle={() => onToggle(client.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Leads */}
        {groupedClients.leads.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Leads ({groupedClients.leads.length})
            </h3>
            <div className="space-y-1">
              {groupedClients.leads.map((client) => (
                <ClientCheckboxItem
                  key={client.id}
                  client={client}
                  isSelected={selectedIds.has(client.id)}
                  onToggle={() => onToggle(client.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Other */}
        {groupedClients.other.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Other ({groupedClients.other.length})
            </h3>
            <div className="space-y-1">
              {groupedClients.other.map((client) => (
                <ClientCheckboxItem
                  key={client.id}
                  client={client}
                  isSelected={selectedIds.has(client.id)}
                  onToggle={() => onToggle(client.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {totalCount === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">
              {searchQuery ? 'No clients found matching your search' : 'No clients available'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Individual client checkbox item
interface ClientCheckboxItemProps {
  client: ClientRecord;
  isSelected: boolean;
  onToggle: () => void;
}

function ClientCheckboxItem({ client, isSelected, onToggle }: ClientCheckboxItemProps) {
  const getStatusBadge = () => {
    switch (client.status) {
      case 'active':
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>;
      case 'lead':
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Lead</span>;
      case 'past':
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">Inactive</span>;
      case 'cold':
        return <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">Cold</span>;
      default:
        return null;
    }
  };

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
        isSelected
          ? 'border-[#5B8E87] bg-green-50'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      {/* Checkbox */}
      <div
        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
          isSelected
            ? 'bg-[#5B8E87] border-[#5B8E87]'
            : 'bg-white border-gray-300'
        }`}
      >
        {isSelected && <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} />}
      </div>

      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
        <UserIcon className="w-5 h-5 text-gray-500" />
      </div>

      {/* Client Info */}
      <div className="flex-1 text-left min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="font-medium text-sm text-gray-900 truncate">{client.name}</p>
          {getStatusBadge()}
        </div>
        <p className="text-xs text-gray-500 truncate">{client.phone}</p>
        {client.goal && (
          <p className="text-xs text-gray-400 truncate mt-0.5">Goal: {client.goal}</p>
        )}
      </div>
    </button>
  );
}
